// ** Modules
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

// ** UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useTheme } from '@mui/material/styles';

// ** Hooks
import { useSearchLogic } from '@/hooks/useSearchLogic';

// ** Components
import Section from '@/components/common/Section';
import FadeInView from '@/components/common/FadeInView';
import ComponentBlocksSearchFilter from '@/components/strapi/Shared/ComponentBlocksSearchFilter';
import ComponentElementsPersonCard from '@/components/strapi/Elements/ComponentElementsPersonCard';

// ** Type
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksTeamBlock as BlocksTeamBlockProps } from '@/types/graphql';

type ComponentBlocksTeamBlockProps = StrapiComponentProps<Omit<BlocksTeamBlockProps, 'id' | 'personName'>>;

const ComponentBlocksTeamBlock = ({
	host,
	teams,
	containerSx,
	teamContent = [],
	teamFilterTools,
	teamSectionTitle,
	teamSearchFilters
}: ComponentBlocksTeamBlockProps) => {
	const theme = useTheme();
	const sp = theme.spacing;
	const { t } = useTranslation();
	const router = useRouter();

	const parsedTeams = React.useMemo(() => (typeof teams === 'string' ? JSON.parse(teams) : teams || []), [teams]);

	const defaultTeamCategory = React.useMemo(() => {
		const teamsList = Array.isArray(parsedTeams) ? parsedTeams : [];

		const priorityDefaults = ['associationTeam', 'boardTeam'];

		const matchedDefault = priorityDefaults.find((category) => teamsList.includes(category));

		return matchedDefault || 'all';
	}, [parsedTeams]);

	const { searchState, searchHandlers } = useSearchLogic(defaultTeamCategory);

	React.useEffect(() => {
		const queryTeam = router.query.team;
		if (typeof queryTeam === 'string') {
			searchHandlers.onCategoryChange(queryTeam);
		}
	}, [router.query.team, searchHandlers.onCategoryChange]);

	const activeTeamGroup = searchState.selectedCategory || defaultTeamCategory;

	const filterOptions = React.useMemo(() => {
		const baseOptions = [
			{
				value: 'all',
				label: teamSearchFilters?.searchFilterCategoryPlaceholder || t('common:all_teams', 'All Teams')
			}
		];

		const teamOptions = parsedTeams.map((groupName: string) => ({
			value: groupName,
			label: t(`common:${groupName}`)
		}));

		return {
			categories: [...baseOptions, ...teamOptions],
			periods: []
		};
	}, [parsedTeams, t, teamSearchFilters]);

	const filteredPeople = React.useMemo(() => {
		let individuals: any[] = [];

		if (activeTeamGroup === 'all') {
			const allPeople = teamContent.flatMap((group) => group?.teamIndividuals || []);
			const seenIds = new Set();
			individuals = allPeople.filter((person) => {
				const id = person?.individual?.documentId;
				if (!id || seenIds.has(id)) return false;
				seenIds.add(id);
				return true;
			});
		} else {
			const currentGroup = teamContent.find((item) => item?.teamGroup === activeTeamGroup);
			individuals = currentGroup?.teamIndividuals || [];
		}

		const searchTerm = searchState.searchTerm?.toLowerCase().trim();
		if (searchTerm) {
			individuals = individuals.filter((person) => {
				const ind = person?.individual;
				if (!ind) return false;
				const fullName = `${ind.firstName || ''} ${ind.lastName || ''}`.toLowerCase();
				return fullName.includes(searchTerm);
			});
		}

		return individuals;
	}, [activeTeamGroup, teamContent, searchState.searchTerm]);

	// Build strict SearchFilterConfig without nulls
	const searchFilterConfig = teamSearchFilters
		? {
				searchFilterShowCategory: teamSearchFilters.searchFilterShowCategory ?? undefined,
				searchFilterShowSearch: teamSearchFilters.searchFilterShowSearch ?? undefined,
				searchFilterShowPeriod: false,
				searchFilterCategoryPlaceholder: teamSearchFilters.searchFilterCategoryPlaceholder ?? undefined,
				searchFilterSearchPlaceholder: teamSearchFilters.searchFilterSearchPlaceholder ?? undefined,
				searchFilterPeriodPlaceholder: teamSearchFilters.searchFilterPeriodPlaceholder ?? undefined,
				showCategory: teamSearchFilters.searchFilterShowCategory ?? undefined,
				showSearch: teamSearchFilters.searchFilterShowSearch ?? undefined,
				showPeriod: false
			}
		: undefined;

	return (
		<Section containerSx={containerSx}>
			{teamSectionTitle && (
				<Typography
					variant="title-lg"
					component="h2"
					sx={{
						mb: 10,
						color: 'common.black',
						fontSize: { xs: '2rem', md: '2.5rem' },
						lineHeight: { xs: '2.5rem', md: '3rem' },
						letterSpacing: { xs: '-0.64px', md: '-1.2px' }
					}}
				>
					{teamSectionTitle}
				</Typography>
			)}

			{teamFilterTools && (
				<FadeInView>
					<Box sx={{ mb: { xs: sp(8), md: sp(12) } }}>
						<ComponentBlocksSearchFilter
							config={searchFilterConfig}
							options={filterOptions}
							state={searchState}
							handlers={searchHandlers}
							backgroundColor={theme.palette.heroLightBackground?.main as string}
						/>
					</Box>
				</FadeInView>
			)}

			<Grid container spacing={{ xs: 4, md: 6 }}>
				{filteredPeople.map((person, index) => (
					<Grid key={person?.individual?.documentId || index} size={{ xs: 12, sm: 6, lg: 3 }}>
						<ComponentElementsPersonCard host={host} {...person} />
					</Grid>
				))}

				{filteredPeople.length === 0 && (
					<Grid size={12}>
						<Box
							sx={{
								py: 10,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								gap: 2,
								color: 'text.secondary'
							}}
						>
							<SearchOffIcon sx={{ fontSize: 56, opacity: 0.4 }} />
							<Typography variant="body-lg" sx={{ fontWeight: 500 }}>
								{t('common:no_persons_found', 'Keine Personen gefunden.')}
							</Typography>
							<Typography variant="body-md" sx={{ opacity: 0.7 }}>
								{t('common:adjust_search_hint', 'Bitte passen Sie Ihre Suche oder Filter an.')}
							</Typography>
						</Box>
					</Grid>
				)}
			</Grid>
		</Section>
	);
};

export default ComponentBlocksTeamBlock;
